import { showToast } from "utils/helpers";

/**
 * Backend error code returned (HTTP 403) when an executive who has switched
 * into another company's read-only view attempts a write.
 */
export const EXECUTIVE_VIEW_READ_ONLY_CODE = "EXECUTIVE_VIEW_READ_ONLY";

/**
 * Stable toast id so rapid repeated writes (e.g. debounced auto-save) collapse
 * into a single read-only info toast instead of stacking N identical toasts.
 */
export const EXECUTIVE_VIEW_READ_ONLY_TOAST_ID = "executive-view-read-only";

const READ_ONLY_TOAST_MESSAGE =
	"You are in read-only view mode — changes can’t be saved while viewing another company.";
const READ_ONLY_TOAST_TITLE = "Read-only";

interface ReadOnlyErrorShape {
	code?: string;
	error?: {
		code?: string;
	};
}

interface ReadOnlyErrorLike {
	code?: string;
	error?: {
		code?: string;
	};
	response?: {
		data?: ReadOnlyErrorShape;
	};
}

/**
 * Extracts the executive read-only error code from either:
 *  - an axios error (`error.response.data`), or
 *  - an already-parsed response body (ky `await response.json()`).
 */
const extractReadOnlyCode = (
	errorLike: ReadOnlyErrorLike | null | undefined
): string | undefined => {
	if (!errorLike) {
		return undefined;
	}

	const data: ReadOnlyErrorShape = errorLike.response?.data ?? errorLike;

	return data?.error?.code ?? data?.code;
};

/**
 * Detects an executive read-only 403 and, if matched, shows a friendly blue
 * info toast (via `showToast(..., success = true)`) instead of a generic error.
 *
 * Returns `true` when it handled a read-only 403 (so callers can skip their own
 * generic error toast), `false` otherwise. Safe to call from multiple layers —
 * the stable `toastId` de-dupes overlapping calls into one toast.
 */
export const handleReadOnly403 = (
	errorLike: ReadOnlyErrorLike | null | undefined
): boolean => {
	if (extractReadOnlyCode(errorLike) !== EXECUTIVE_VIEW_READ_ONLY_CODE) {
		return false;
	}

	showToast(
		READ_ONLY_TOAST_MESSAGE,
		READ_ONLY_TOAST_TITLE,
		true,
		EXECUTIVE_VIEW_READ_ONLY_TOAST_ID,
		"warning"
	);

	return true;
};
